/** @prettier */

import {Grid, Box, Container, Skeleton, IconButton} from '@mui/material';
import {useEffect, useState, useRef} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {isUndefined} from 'lodash';
import {useTranslation} from 'react-i18next';
import {TFunction} from 'i18next';

import {routeList} from '../PrivateRoute';
//API import
import {
  getCompositionsLayoutImage,
  getCompositionsGroupsByGroupID,
  getStatesSitesSl,
  getCompositionsSitesBySiteID,
} from '../../API/API_Collection';
//interface import
import * as apiIF from '../../Type/api';
//type import
import {
  groupIDInfoType,
  SLOpeStatusType,
  layoutUnitListType,
} from '../../Type/TypeDef';
//Style import
import '../../css/common/common.css';

import {iconBlack, iconWhite} from '../../define';

type IconListType = {
  iconTop: number;
  iconLeft: number;
  unitID: string;
  unitName: string;
};

const GroupDetail = (props: {
  selectedItem: string;
  unitTypeList: layoutUnitListType;
  setSelectedItem: React.Dispatch<React.SetStateAction<string>>;
}) => {
  const trans = useTranslation().t; //翻訳関数
  const navigate = useNavigate();
  const layoutElement = useRef<HTMLDivElement>(null); //アイコン表示用要素
  const imageElement = useRef<HTMLImageElement>(null); //画像表示用要素
  //パスパラメータ取得
  const {siteID, targetID: groupID} = useParams();

  const [layoutImg, setLayoutImg] = useState<string | null>(null); //レイアウト画像
  const [iconList, setIconList] = useState<IconListType[]>([]); //アイコンの座標一覧
  const [displayGroupInfo, setDisplayGroupInfo] =
    useState<groupIDInfoType | null>(null); //グループの情報
  const [slOpeStatus, setSLOpeStatus] = useState<SLOpeStatusType | null>(null); //空調機の状態値
  const [now, setNow] = useState(new Date());
  const [layoutImgWH, setLayoutImgWH] = useState({width: 0, height: 0}); //レイアウトの縦横

  useEffect(() => {
    //パスパラメータを取得できない場合
    if (isUndefined(siteID)) return;
    //サイトの情報を取得
    getCompositionsSitesBySiteID(siteID)
      .then(response => {
        if (response['site-authority'] === 'none') {
          //サイトの権限がnoneの場合はリダイレクト
          navigate(routeList.mySite.path);
        }
      })
      .catch(() => {
        //サイトの情報取得に失敗したらリダイレクト
        navigate(routeList.mySite.path);
      });
  }, [siteID]);

  //グループレイアウト画像の取得
  useEffect(() => {
    //パスパラメータを取得できない場合
    if (isUndefined(siteID) || isUndefined(groupID)) return;

    //レイアウト画像を初期化
    setLayoutImg(null);

    //レイアウト画像取得
    getCompositionsLayoutImage(siteID, groupID).then(response => {
      setLayoutImg(response['layout-image']);
    });
  }, [groupID, siteID]);

  //レイアウト画像を表示
  useEffect(() => {
    //画像・要素を取得できない場合
    if (imageElement.current === null || layoutImg === null) return;

    imageElement.current.src = layoutImg;

    if (layoutElement === null || layoutElement.current == null) return;
    const layoutWidth = layoutElement.current.offsetWidth; //レイアウト画像の幅
    const layoutHeight = layoutElement.current.offsetHeight; //レイアウト画像の高さ
    setLayoutImgWH({width: layoutWidth, height: layoutHeight});
  }, [
    imageElement.current?.offsetWidth,
    imageElement.current?.offsetHeight,
    layoutImg,
  ]);

  //アイコンの座標を取得
  useEffect(() => {
    //グループ情報をできない場合
    if (displayGroupInfo === null) return;

    /**
     * アイコンの表示する横位置を算出
     * @param unitPosX X座標（相対座標）
     * @param width 基準となる横幅
     * @returns アイコンの表示する左上からの位置(割合)
     */
    const iconPosX = (unitPosX: number, width: number) => {
      // レイアウト設定の横のpixel数で比率割掛け
      return (700 / width) * unitPosX;
    };
    /**
     * アイコンの表示する縦位置を算出
     * @param unitPosY Y座標（相対座標）
     * @param height 基準となる高さ
     * @returns アイコンの表示する左上からの位置(割合)
     */
    const iconPosY = (unitPosY: number, height: number) => {
      // レイアウト設定の横のpixel数で比率割掛け
      return (500 / height) * unitPosY;
    };

    //アイコンの座標を初期化
    setIconList([]);

    setIconList(
      displayGroupInfo['unit-informations'].reduce((result, info) => {
        const xCoordinate = Number(info['x-coordinate']);
        const yCoordinate = Number(info['y-coordinate']);
        //初期位置のアイコンはスキップ
        if (xCoordinate < 0 || yCoordinate < 0) {
          return result;
        }

        result.push({
          unitID: info['unit-id'],
          unitName: info['unit-name'],
          iconLeft: iconPosX(xCoordinate, layoutImgWH.width),
          iconTop: iconPosY(yCoordinate, layoutImgWH.height),
        });
        return result;
      }, [] as IconListType[])
    );
  }, [displayGroupInfo, layoutImgWH]);

  //グループに所属するユニットの運転停止/運転モード情報を取得
  useEffect(() => {
    //サイトID,グループIDがundefinedの場合は処理しない
    if (isUndefined(siteID) || isUndefined(groupID)) return;

    //表示するグループのユニットのみに絞る
    const query = {
      'group-id': groupID,
      'fields': 'run-stop,operation-mode',
    };
    //指定したグループに所属する、室内機の状態値を取得
    getStatesSitesSl(siteID, query)
      .then(response => {
        let slStateTemp: SLOpeStatusType = {};
        response['sl-states'][0]['states'].forEach(
          (state: apiIF.resParameters.slStates) => {
            //ユニットID毎に、運転停止、運転モードの情報をまとめる
            slStateTemp[state['unit-id']] = {
              runStop: state['run-stop'] !== undefined ? state['run-stop'] : '',
              operationMode:
                state['operation-mode'] !== undefined
                  ? state['operation-mode']
                  : '',
            };
          }
        );
        setSLOpeStatus(slStateTemp);
      })
      .catch(err => {
        setSLOpeStatus(null);
      });
  }, [groupID, siteID]);

  //グループの情報を取得
  useEffect(() => {
    if (isUndefined(siteID) || isUndefined(groupID)) return;

    //グループ情報取得
    getCompositionsGroupsByGroupID(siteID, groupID)
      .then(response => {
        setDisplayGroupInfo(response);
      })
      .catch(err => {
        setDisplayGroupInfo(null);
        setSLOpeStatus(null);
      });
  }, [groupID, siteID]);

  // 定期更新
  useEffect(() => {
    const intervalId = setInterval(() => {
      //サイトID,グループIDがundefinedの場合は処理しない
      if (isUndefined(siteID) || isUndefined(groupID)) return;

      setNow(new Date());

      //表示するグループのユニットのみに絞る
      const query = {
        'group-id': groupID,
        'fields': 'run-stop,operation-mode',
      };
      //指定したグループに所属する、室内機の状態値を取得
      getStatesSitesSl(siteID, query)
        .then(response => {
          let slStateTemp: SLOpeStatusType = {};
          response['sl-states'][0]['states'].forEach(
            (state: apiIF.resParameters.slStates) => {
              //ユニットID毎に、運転停止、運転モードの情報をまとめる
              slStateTemp[state['unit-id']] = {
                runStop:
                  state['run-stop'] !== undefined ? state['run-stop'] : '',
                operationMode:
                  state['operation-mode'] !== undefined
                    ? state['operation-mode']
                    : '',
              };
            }
          );
          setSLOpeStatus(slStateTemp);
        })
        .catch(err => {
          setSLOpeStatus(null);
        });
    }, 60000);

    /* 60秒毎にタイマを削除 */
    return function () {
      clearInterval(intervalId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [now]);

  if (displayGroupInfo === null) return <></>;
  return (
    <>
      {/** 以下のid（groupLayoutBox）は空調機名の位置の調整に使用するため消さないこと */}
      <Container fixed id="groupLayoutBox">
        <Grid
          container
          rowSpacing={3}
          justifyContent="center"
          alignItems="center">
          {/** フロア名、グループ名 */}
          <Grid item xs={12} className="groupLayoutArea__title">
            <Box
              title={displayGroupInfo['group-name']}
              className="groupLayoutArea__title__name">
              {trans(
                `SLGroupDetail.floor.floor${displayGroupInfo.floor.replace(
                  '-',
                  'B'
                )}`
              )}
              &nbsp;-&nbsp;
              {displayGroupInfo['group-name']}
            </Box>
          </Grid>
          {/** グループ画像、ユニットアイコン */}
          <Grid item xs>
            <Box className="groupLayoutArea__layout">
              {layoutImg === null ? (
                <Skeleton variant="rectangular">
                  <Box className="groupLayoutArea__layout__box" />
                </Skeleton>
              ) : (
                //グループ画像とアイコンを表示
                <Box
                  className="groupLayoutArea__layout__box"
                  ref={layoutElement}>
                  <img
                    className="groupLayoutArea__layout__image"
                    ref={imageElement}
                    alt={`${trans(
                      `SLGroupDetail.image.alternativeText.groupLayoutImage`
                    )}`}
                  />
                  {slOpeStatus === null ? (
                    <></>
                  ) : (
                    iconList.map(info => {
                      const unitTypeInfo = props.unitTypeList.find(
                        item => item.unitID === info.unitID
                      );
                      if (unitTypeInfo === undefined) return <></>;
                      return (
                        <UnitIcon
                          key={info.unitID}
                          states={slOpeStatus}
                          iconInfo={info}
                          selectedItem={props.selectedItem}
                          unitType={unitTypeInfo.unitType}
                          setSelectedItem={props.setSelectedItem}
                        />
                      );
                    })
                  )}
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
      </Container>
    </>
  );
};

/**
 * 運転時のアイコンのデザイン決定
 * @param unitType SL通信の室内機、またはインターフェースの型式
 * @returns 空調機アイコンのデザイン
 */
const selectRunIconStyle = (unitType: layoutUnitListType[0]['unitType']) => {
  switch (unitType) {
    case 'AC':
    default:
      return {
        src: iconWhite.AC_ON.src,
        transKeyForAlt: `SLGroupDetail.image.alternativeText.${iconWhite.AC_ON.alt}`,
      };
    case 'FDT':
      return {
        src: iconWhite.FDT_ON.src,
        transKeyForAlt: `SLGroupDetail.image.alternativeText.${iconWhite.FDT_ON.alt}`,
      };
    case 'FDTS':
      return {
        src: iconWhite.FDTS_ON.src,
        transKeyForAlt: `SLGroupDetail.image.alternativeText.${iconWhite.FDTS_ON.alt}`,
      };
    case 'FDTW':
      return {
        src: iconWhite.FDTW_ON.src,
        transKeyForAlt: `SLGroupDetail.image.alternativeText.${iconWhite.FDTW_ON.alt}`,
      };
    case 'FDE':
      return {
        src: iconWhite.FDE_ON.src,
        transKeyForAlt: `SLGroupDetail.image.alternativeText.${iconWhite.FDE_ON.alt}`,
      };
    case 'FDF':
      return {
        src: iconWhite.FDF_ON.src,
        transKeyForAlt: `SLGroupDetail.image.alternativeText.${iconWhite.FDF_ON.alt}`,
      };
    case 'FDFW':
      return {
        src: iconWhite.FDFW_ON.src,
        transKeyForAlt: `SLGroupDetail.image.alternativeText.${iconWhite.FDFW_ON.alt}`,
      };
    case 'FDK':
      return {
        src: iconWhite.FDK_ON.src,
        transKeyForAlt: `SLGroupDetail.image.alternativeText.${iconWhite.FDK_ON.alt}`,
      };
    case 'FDU':
      return {
        src: iconWhite.FDU_ON.src,
        transKeyForAlt: `SLGroupDetail.image.alternativeText.${iconWhite.FDU_ON.alt}`,
      };
    case 'VENT':
      return {
        src: iconWhite.VENTILATOR_UNIT_ON.src,
        transKeyForAlt: `SLGroupDetail.image.alternativeText.${iconWhite.VENTILATOR_UNIT_ON.alt}`,
      };
    case 'LIGHT':
      return {
        src: iconWhite.LIGHT_UNIT_ON.src,
        transKeyForAlt: `SLGroupDetail.image.alternativeText.${iconWhite.LIGHT_UNIT_ON.alt}`,
      };
  }
};
/**
 * 停止時のアイコンのデザイン決定
 * @param unitType SL通信の室内機、またはインターフェースの型式
 * @returns 空調機アイコンのデザイン
 */
const selectStopIconStyle = (unitType: layoutUnitListType[0]['unitType']) => {
  switch (unitType) {
    case 'AC':
    default:
      return {
        src: iconBlack.AC_OFF.src,
        transKeyForAlt: `SLGroupDetail.image.alternativeText.${iconBlack.AC_OFF.alt}`,
      };
    case 'FDT':
      return {
        src: iconBlack.FDT_OFF.src,
        transKeyForAlt: `SLGroupDetail.image.alternativeText.${iconBlack.FDT_OFF.alt}`,
      };
    case 'FDTS':
      return {
        src: iconBlack.FDTS_OFF.src,
        transKeyForAlt: `SLGroupDetail.image.alternativeText.${iconBlack.FDTS_OFF.alt}`,
      };
    case 'FDTW':
      return {
        src: iconBlack.FDTW_OFF.src,
        transKeyForAlt: `SLGroupDetail.image.alternativeText.${iconBlack.FDTW_OFF.alt}`,
      };
    case 'FDE':
      return {
        src: iconBlack.FDE_OFF.src,
        transKeyForAlt: `SLGroupDetail.image.alternativeText.${iconBlack.FDE_OFF.alt}`,
      };
    case 'FDF':
      return {
        src: iconBlack.FDF_OFF.src,
        transKeyForAlt: `SLGroupDetail.image.alternativeText.${iconBlack.FDF_OFF.alt}`,
      };
    case 'FDFW':
      return {
        src: iconBlack.FDFW_OFF.src,
        transKeyForAlt: `SLGroupDetail.image.alternativeText.${iconBlack.FDFW_OFF.alt}`,
      };
    case 'FDK':
      return {
        src: iconBlack.FDK_OFF.src,
        transKeyForAlt: `SLGroupDetail.image.alternativeText.${iconBlack.FDK_OFF.alt}`,
      };
    case 'FDU':
      return {
        src: iconBlack.FDU_OFF.src,
        transKeyForAlt: `SLGroupDetail.image.alternativeText.${iconBlack.FDU_OFF.alt}`,
      };
    case 'VENT':
      return {
        src: iconBlack.VENTILATOR_UNIT_OFF.src,
        transKeyForAlt: `SLGroupDetail.image.alternativeText.${iconBlack.VENTILATOR_UNIT_OFF.alt}`,
      };
    case 'LIGHT':
      return {
        src: iconBlack.LIGHT_UNIT_OFF.src,
        transKeyForAlt: `SLGroupDetail.image.alternativeText.${iconBlack.LIGHT_UNIT_OFF.alt}`,
      };
  }
};

/**
 * 運転状態アイコンのデザインの決定
 * @param runStop 運転状態
 * @param operationMode 運転モード
 * @param unitType SL通信の室内機、またはインターフェースの型式
 * @returns デザイン
 */
const modeIconStyle = (
  runStop: '' | 'run' | 'stop',
  operationMode: '' | 'auto' | 'dehumidify' | 'cooling' | 'fan' | 'heating',
  unitType: layoutUnitListType[0]['unitType'],
  trans: TFunction
) => {
  let result = {
    class: 'groupLayoutArea__layout__box__iconBox__icon__border--stop',
    src: iconBlack.MISSING.src,
    alt: trans(`SLGroupDetail.image.alternativeText.acStop`),
  };
  //型式情報が存在しない場合
  if (unitType === undefined) return result;
  if (runStop === 'run') {
    //運転状態時のユニットアイコンのデザイン
    const runIconStyle = selectRunIconStyle(unitType);
    result.src = runIconStyle.src;
    result.alt = trans(runIconStyle.transKeyForAlt);
    switch (operationMode) {
      case 'cooling':
        result.class =
          'groupLayoutArea__layout__box__iconBox__icon__border--cooling';
        break;
      case 'heating':
        result.class =
          'groupLayoutArea__layout__box__iconBox__icon__border--heating';
        break;
      case 'fan':
        result.class =
          'groupLayoutArea__layout__box__iconBox__icon__border--fan';
        break;
      case 'dehumidify':
        result.class =
          'groupLayoutArea__layout__box__iconBox__icon__border--dehumidify';
        break;
      case 'auto':
        result.class =
          'groupLayoutArea__layout__box__iconBox__icon__border--auto';
        break;
      default:
        result.class =
          'groupLayoutArea__layout__box__iconBox__icon__border--stop';
        result.src = iconBlack.AC_ON.src;
    }
  } else if (runStop === 'stop') {
    //停止状態時のユニットアイコンのデザイン
    const stopIconStyle = selectStopIconStyle(unitType);
    result.src = stopIconStyle.src;
    result.alt = trans(stopIconStyle.transKeyForAlt);
  }
  return result;
};

//画像(アイコン)読み込み時
const imgLoadIcon = (unitId: string) => {
  //操作画面-グループ画面間で選択状態に差異を生じさせないため、連動するチェックボックスの状態リセット
  let elems = document.getElementsByName(
    unitId + '_checkbox'
  ) as NodeListOf<HTMLInputElement>;
  for (let elem of elems) {
    elem.checked = false;
  }
};

/**
 * 空調機アイコンを表示
 * @param props props
 * @returns JSX
 */
const UnitIcon = (props: {
  states: SLOpeStatusType;
  iconInfo: IconListType;
  selectedItem: string;
  unitType: layoutUnitListType[0]['unitType'];
  setSelectedItem: React.Dispatch<React.SetStateAction<string>>;
}) => {
  const trans = useTranslation().t; //翻訳関数

  //ユニットアイコンのデザイン
  const {
    class: iconClassName,
    src: iconSrc,
    alt: iconAlt,
  } = modeIconStyle(
    props.states[props.iconInfo.unitID].runStop,
    props.states[props.iconInfo.unitID].operationMode,
    props.unitType,
    trans
  );

  return (
    <Box
      className="groupLayoutArea__layout__box__iconBox"
      style={{
        top: `${props.iconInfo.iconTop}%`,
        left: `${props.iconInfo.iconLeft}%`,
      }}
      id={`${props.iconInfo.unitID}Icon`}
      onLoad={() => imgLoadIcon(props.iconInfo.unitID)}>
      <Grid container>
        {/* ユニットアイコン */}
        <Grid item xs="auto">
          <IconButton
            size="small"
            onClick={() => props.setSelectedItem(props.iconInfo.unitID)}>
            <Box
              className={`${iconClassName} ${
                props.iconInfo.unitID === props.selectedItem
                  ? 'groupLayoutArea__layout__box__iconBox__icon--highlight'
                  : ''
              }`}>
              <img
                src={iconSrc}
                alt={iconAlt}
                className="groupLayoutArea__layout__box__iconBox__icon__border__image"
              />
            </Box>
          </IconButton>
        </Grid>
        {/* ユニット名称 */}
        <Grid item xs={12}>
          {/** 以下のid（*****_unitName）は空調機名の位置の調整に使用するため消さないこと */}
          <Box
            title={props.iconInfo.unitName}
            id={`${props.iconInfo.unitID}_unitName`}
            className="groupLayoutArea__layout__box__iconBox__unitNameBox">
            <Box
              component="span"
              className="groupLayoutArea__layout__box__iconBox__unitNameBox__text">
              {props.iconInfo.unitName}
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};
export default GroupDetail;
